/**
 * Background-MUSIC "intent" flag — a durable record that music WAS sounding, so
 * it can resume across a cross-app navigation or a page refresh.
 *
 * Root cause (tester B2, 2026-08-31): on WEB, the parent landing (web-app) and
 * the child lessons (web-child) are SEPARATE SPAs served by SEPARATE ECS
 * services. A landing→lesson navigation is a HARD cross-app load
 * (`window.location.replace` → /learn* served by web-child): web-app's
 * BackgroundMusicProvider unmounts (dispose → stop kills the bed) and web-child
 * boots a FRESH provider in a fresh document with NO user gesture — so its
 * mount-time autoplay is browser-BLOCKED. A plain browser refresh is the same
 * shape: a cold document with no gesture. There was no persisted signal that
 * "music was intended to be playing", so every cold load started silent and
 * stayed silent until the child happened to trigger the exact re-assert path.
 *
 * This module is the anti-drift SSOT for that signal: both apps' providers
 * write `true` the instant music ACTUALLY starts, and write `false` only when
 * the user turns music OFF. On a fresh provider mount, `true` here means "keep
 * the first-gesture unlock armed and resume on the very first interaction"
 * (which in a lesson happens almost immediately). It NEVER auto-starts audio
 * without a gesture — browser autoplay policy forbids that, and this flag does
 * not attempt to work around it.
 *
 * The flag is intentionally a PLAIN localStorage boolean (not part of the child
 * prefs object) so it is a lightweight, app-agnostic playback-state hint that
 * never collides with the user's enabled/volume/theme preferences. The prefs
 * `backgroundMusicEnabled` toggle remains the authority for whether music is
 * ALLOWED to play; this flag only records whether it was ACTUALLY playing.
 */

// v1 — bump the suffix if the semantics ever change so a stale value can't be
// misread by a newer build.
const MUSIC_INTENT_KEY = 'lwa_music_intent_v1';

function safeStorage(): Storage | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    return localStorage;
  } catch {
    // Storage can throw (Safari private mode / disabled cookies) — degrade to
    // "no persisted intent" rather than crashing the provider.
    return null;
  }
}

/**
 * Has music been recorded as actively playing (in this browser profile)?
 * Read on a fresh provider mount to decide whether to keep the first-gesture
 * unlock armed for an immediate resume after a refresh / cross-app nav.
 */
export function getMusicIntent(): boolean {
  const s = safeStorage();
  if (!s) return false;
  try {
    return s.getItem(MUSIC_INTENT_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Record whether music is intended to be playing.
 *  - Call `setMusicIntent(true)` ONLY when playback actually started (the
 *    provider's real-play confirmation), so a blocked/failed autoplay never
 *    sets a false-positive intent.
 *  - Call `setMusicIntent(false)` when the user disables music via prefs, so a
 *    parent who turned music OFF is never resurrected on the next load.
 */
export function setMusicIntent(intended: boolean): void {
  const s = safeStorage();
  if (!s) return;
  try {
    if (intended) s.setItem(MUSIC_INTENT_KEY, '1');
    else s.removeItem(MUSIC_INTENT_KEY);
  } catch {
    /* ignore — best-effort persistence */
  }
}

/** Exposed for tests / tooling that need the exact key. */
export const MUSIC_INTENT_STORAGE_KEY = MUSIC_INTENT_KEY;
